// packages/frontend/src/utils/piiScrubber.ts
//
// Single scrubber applied to analytics event properties before they reach
// ANY sink (Firebase Analytics or PostHog). Redacts names, emails, phone
// numbers, and client/child/patient identifiers so a forgotten hash at a
// call site cannot leak PHI.

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_REGEX = /\+?\d[\d\s().-]{6,}/;
const MAX_STRING_LENGTH = 200;
const REDACTED = '[redacted]';

// Substrings (checked against the key with separators stripped) that mark a
// property as carrying PHI/PII. Keys ending in "hash" are exempt: a value
// already hashed at the call site (e.g. client_id_hash) is safe to forward.
const BLOCKED_KEY_PATTERNS = [
  'email',
  'phone',
  'notes',
  'name',
  'address',
  'clientid',
  'childid',
  'patientid',
];

const BLOCKED_EXACT_KEYS = new Set([
  'user_id',
  'userid',
  'gender',
  'ethnicity',
  'diagnosis',
  'prompt',
  'error_message',
  'spdsubtype',
  'sensory_modality',
  'age',
]);

function isBlockedKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  if (lowerKey.endsWith('hash')) {
    return false;
  }
  if (BLOCKED_EXACT_KEYS.has(lowerKey) || BLOCKED_EXACT_KEYS.has(lowerKey.replace(/[_-]/g, ''))) {
    return true;
  }
  const normalized = lowerKey.replace(/[_-]/g, '');
  return BLOCKED_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function scrubAnalyticsProperties(
  input: Record<string, unknown>
): Record<string, unknown> {
  const redacted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(input)) {
    if (isBlockedKey(key)) {
      redacted[key] = REDACTED;
      continue;
    }

    if (typeof value === 'string') {
      if (EMAIL_REGEX.test(value) || PHONE_REGEX.test(value)) {
        redacted[key] = REDACTED;
        continue;
      }
      if (value.length > MAX_STRING_LENGTH) {
        redacted[key] = value.slice(0, MAX_STRING_LENGTH);
        continue;
      }
    }

    redacted[key] = value;
  }

  return redacted;
}
